import * as React from "react";
import { Link } from "react-router-dom";
import {
  PageHeader,
  PageHeaderTop,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatCard,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from "@eduviaos/ui-kit";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  ExternalLink,
  Phone,
  RefreshCw,
  UserRound,
} from "lucide-react";
import type { AdmissionFunnelStage, CalendarItem, StaffRosterItem } from "@eduviaos/shared";
import { useCalendar, useFunnel, useStaffRoster } from "@/hooks/useAdmissions";
import { useAuth } from "@/auth/AuthProvider";
import { AdmissionsSubNav } from "@/components/AdmissionsSubNav";
import { ExportCsvErrorBanner } from "@/components/ExportCsvErrorBanner";
import { useCsvExport } from "@/hooks/useCsvExport";
import type { CsvCellValue } from "@/lib/csv";
import { ADMISSIONS_CALENDAR_CSV_COLUMNS, mapCalendarItemToCsvRow } from "@/lib/admissionsCsv";

/**
 * AdmissionsCalendarPage — Phase D3.
 *
 * The follow-up calendar is the IA's "morning rounds" view: sit down
 * with a coffee, see who's overdue / due today / due this week, and
 * tear through them in order. Backed by `GET /admissions/calendar`
 * which returns a single window with overdue rows folded in.
 *
 * UX principles:
 *   - Group items into Overdue / Today / This Week / Later. Overdue
 *     and Today get a destructive / amber accent so the IA can't miss
 *     them when scanning. Later is collapsible to keep the visible
 *     surface tight.
 *   - Each item links straight to the enquiry detail (the IA's
 *     workhorse) — no row-click-opens-sheet. We respect anchor
 *     navigation so cmd-click opens in a new tab.
 *   - Stat strip up top mirrors the one on the dashboard so KPIs are
 *     consistent across surfaces.
 */

const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: "Next 7 days" },
  { value: 14, label: "Next 14 days" },
  { value: 30, label: "Next 30 days" },
  { value: 60, label: "Next 60 days" },
];

interface Group {
  key: "overdue" | "today" | "tomorrow" | "this_week" | "later";
  label: string;
  description: string;
  items: CalendarItem[];
  /** Tone hints drive the card stripe + icon. */
  tone: "danger" | "warning" | "info" | "neutral";
}

function bucket(item: CalendarItem, now: Date): Group["key"] {
  const at = new Date(item.next_followup_at);
  if (at < now) return "overdue";
  const dayMs = 86_400_000;
  if (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  )
    return "today";
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (
    at.getFullYear() === tomorrow.getFullYear() &&
    at.getMonth() === tomorrow.getMonth() &&
    at.getDate() === tomorrow.getDate()
  )
    return "tomorrow";
  if (at.getTime() - now.getTime() <= dayMs * 7) return "this_week";
  return "later";
}

const GROUP_TONE: Record<Group["key"], Group["tone"]> = {
  overdue: "danger",
  today: "warning",
  tomorrow: "info",
  this_week: "info",
  later: "neutral",
};

const GROUP_LABEL: Record<Group["key"], { label: string; description: string }> = {
  overdue: { label: "Overdue", description: "Promised but missed — call today." },
  today: { label: "Today", description: "Due before midnight your time." },
  tomorrow: { label: "Tomorrow", description: "Plan ahead — block calendar time." },
  this_week: { label: "This week", description: "Due in the next seven days." },
  later: { label: "Later", description: "Beyond the seven-day horizon." },
};

const ORDER: Group["key"][] = ["overdue", "today", "tomorrow", "this_week", "later"];

export function AdmissionsCalendarPage() {
  const [windowDays, setWindowDays] = React.useState<number>(14);
  const { data, isLoading, isFetching, refetch, isError } = useCalendar({
    window_days: windowDays,
    include_overdue: true,
  });
  const { data: roster } = useStaffRoster();
  const { data: funnel } = useFunnel();
  const { user } = useAuth();
  const tenantSlug = user?.tenant_id ?? null;

  const items = React.useMemo(() => data?.items ?? [], [data?.items]);
  const overdueCount = data?.overdue_count ?? 0;
  const totalCount = items.length;

  const groups = React.useMemo<Group[]>(() => {
    const now = new Date();
    const buckets: Record<Group["key"], CalendarItem[]> = {
      overdue: [],
      today: [],
      tomorrow: [],
      this_week: [],
      later: [],
    };
    for (const item of items) buckets[bucket(item, now)].push(item);
    for (const k of ORDER) {
      buckets[k].sort(
        (a, b) => new Date(a.next_followup_at).getTime() - new Date(b.next_followup_at).getTime(),
      );
    }
    return ORDER.filter((k) => buckets[k].length > 0).map((k) => ({
      key: k,
      label: GROUP_LABEL[k].label,
      description: GROUP_LABEL[k].description,
      tone: GROUP_TONE[k],
      items: buckets[k],
    }));
  }, [items]);

  const todayCount = groups.find((g) => g.key === "today")?.items.length ?? 0;

  // D2.d / E1.a — every row's bucket label is computed against the same
  // pinned `now` that <useCsvExport> hands us, so the CSV and the visible
  // groupings stay in lockstep even if a follow-up rolls past midnight
  // between click and serialization.
  const csvExport = useCsvExport({
    filenamePrefix: "admissions-calendar",
    tenantSlug,
    disabled: isLoading || items.length === 0,
    buildRows: (now) => {
      const stageMap: ReadonlyMap<string, AdmissionFunnelStage> = new Map(
        (funnel?.stages ?? []).map((s) => [s.stage_key, s]),
      );
      const rosterMap: ReadonlyMap<string, StaffRosterItem> = new Map(
        (roster?.items ?? []).map((m) => [m.user_id, m]),
      );
      const rows: ReadonlyArray<ReadonlyArray<CsvCellValue>> = items.map((item) =>
        mapCalendarItemToCsvRow(item, { stageMap, rosterMap, now }),
      );
      return { columns: ADMISSIONS_CALENDAR_CSV_COLUMNS, rows };
    },
  });

  return (
    <>
      <AdmissionsSubNav />
      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>Follow-up calendar</PageHeaderHeading>
            <PageHeaderDescription>
              Every scheduled outreach in one place. Overdue rows lead — clear them first.
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
              <SelectTrigger className="h-9 w-[160px]" aria-label="Window">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOW_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={csvExport.handleExport}
              disabled={isLoading || csvExport.isExporting || items.length === 0}
              aria-label="Download calendar as CSV"
              data-testid="admissions-calendar-export-csv"
            >
              <Download className="size-4" aria-hidden="true" />
              {csvExport.isExporting ? "Preparing…" : "Download CSV"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              aria-label="Refresh calendar"
            >
              <RefreshCw
                className={cn("size-4", isFetching && "animate-spin")}
                aria-hidden="true"
              />
              Refresh
            </Button>
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      <ExportCsvErrorBanner
        error={csvExport.exportError}
        onDismiss={csvExport.dismissError}
        variant="card"
        testid="admissions-calendar-export-error"
        className="mt-6"
      />

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<AlertCircle className="size-4" aria-hidden="true" />}
          label="Overdue"
          value={overdueCount.toLocaleString()}
          caption="Schedule slipped — clear first"
          tone={overdueCount > 0 ? "amber" : "neutral"}
        />
        <StatCard
          icon={<Clock className="size-4" aria-hidden="true" />}
          label="Due today"
          value={todayCount.toLocaleString()}
          caption="Before midnight your time"
          tone={todayCount > 0 ? "blue" : "neutral"}
        />
        <StatCard
          icon={<CalendarClock className="size-4" aria-hidden="true" />}
          label="In this window"
          value={totalCount.toLocaleString()}
          caption={`Within the next ${windowDays} days + overdue`}
          tone="violet"
        />
      </div>

      <div className="mt-6 space-y-6">
        {isLoading ? (
          <CalendarSkeleton />
        ) : isError ? (
          <Card>
            <CardContent className="flex items-center gap-3 py-12">
              <AlertCircle className="text-destructive size-5 shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <p className="text-foreground text-sm font-medium">Couldn't load the calendar</p>
                <p className="text-muted-foreground text-xs">
                  Try refreshing — if it keeps happening, check your network.
                </p>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="size-4" aria-hidden="true" />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : groups.length === 0 ? (
          <EmptyCalendar windowDays={windowDays} />
        ) : (
          groups.map((g) => <CalendarGroup key={g.key} group={g} roster={roster?.items ?? []} />)
        )}
      </div>
    </>
  );
}

// ── Group rendering ─────────────────────────────────────────────────────────

const TONE_CLASS: Record<Group["tone"], { bar: string; chip: string }> = {
  danger: {
    bar: "bg-rose-500",
    chip: "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  },
  warning: {
    bar: "bg-amber-500",
    chip: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  },
  info: {
    bar: "bg-sky-500",
    chip: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",
  },
  neutral: {
    bar: "bg-muted-foreground/40",
    chip: "bg-muted/60 text-muted-foreground border-border/60",
  },
};

interface CalendarGroupProps {
  group: Group;
  roster: { user_id: string; full_name: string | null; email: string }[];
}

function CalendarGroup({ group, roster }: CalendarGroupProps) {
  const tone = TONE_CLASS[group.tone];
  return (
    <Card data-testid={`calendar-group-${group.key}`}>
      <CardHeader className="border-border/60 border-b pb-4">
        <CardTitle className="flex items-center gap-3 text-sm">
          <span className={cn("inline-flex size-2 rounded-full", tone.bar)} aria-hidden="true" />
          <span className="text-foreground font-semibold">{group.label}</span>
          <span
            className={cn("rounded-full border px-2 py-0.5 text-[10px] tabular-nums", tone.chip)}
          >
            {group.items.length}
          </span>
          <span className="text-muted-foreground ml-auto text-xs font-normal">
            {group.description}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <ul className="divide-border/60 divide-y">
          {group.items.map((item) => (
            <CalendarRow key={item.enquiry_id} item={item} tone={group.tone} roster={roster} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function CalendarRow({
  item,
  tone,
  roster,
}: {
  item: CalendarItem;
  tone: Group["tone"];
  roster: { user_id: string; full_name: string | null; email: string }[];
}) {
  const at = new Date(item.next_followup_at);
  const time = at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  const date = at.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const owner = item.assigned_to ? roster.find((r) => r.user_id === item.assigned_to) : null;
  const ownerLabel = owner ? owner.full_name?.trim() || owner.email : "Unassigned";
  const stripeBg = TONE_CLASS[tone].bar;

  return (
    <li className="hover:bg-muted/40 group relative flex items-start gap-3 px-4 py-3 transition-colors">
      <span className={cn("absolute inset-y-0 left-0 w-0.5", stripeBg)} aria-hidden="true" />
      <div className="flex w-[88px] shrink-0 flex-col items-start pl-2">
        <span className="text-foreground text-xs font-medium tabular-nums">{time}</span>
        <span className="text-muted-foreground text-[10px] tabular-nums">{date}</span>
      </div>

      <div className="min-w-0 flex-1">
        <Link
          to={`/admissions/${item.enquiry_id}`}
          className="text-foreground hover:text-primary inline-flex items-center gap-1 truncate text-sm font-medium transition-colors"
          data-testid={`calendar-link-${item.enquiry_id}`}
        >
          <span className="truncate">{item.student_name}</span>
          <ExternalLink
            className="size-3 opacity-0 transition-opacity group-hover:opacity-100"
            aria-hidden="true"
          />
        </Link>
        <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
          {item.parent_name && (
            <span className="inline-flex items-center gap-1">
              <UserRound className="size-3" aria-hidden="true" />
              {item.parent_name}
            </span>
          )}
          {item.parent_phone && (
            <a
              href={`tel:${item.parent_phone}`}
              className="hover:text-foreground inline-flex items-center gap-1"
            >
              <Phone className="size-3" aria-hidden="true" />
              {item.parent_phone}
            </a>
          )}
        </div>
        {item.next_followup_note && (
          <p className="text-muted-foreground mt-1 line-clamp-2 text-xs leading-relaxed">
            {item.next_followup_note}
          </p>
        )}
      </div>

      <div className="hidden flex-col items-end gap-1 sm:flex">
        <span className="text-muted-foreground text-[10px] uppercase tracking-wider">Owner</span>
        <span className="text-foreground text-xs font-medium">{ownerLabel}</span>
      </div>

      <ChevronRight className="text-muted-foreground/40 mt-1 size-4 shrink-0" aria-hidden="true" />
    </li>
  );
}

function CalendarSkeleton() {
  return (
    <div className="space-y-4">
      {[0, 1, 2].map((i) => (
        <Card key={i}>
          <CardHeader>
            <div className="bg-muted h-4 w-32 animate-pulse rounded-full" />
          </CardHeader>
          <CardContent className="space-y-3 pt-3">
            {[0, 1, 2].map((j) => (
              <div key={j} className="flex gap-3">
                <div className="bg-muted h-10 w-16 animate-pulse rounded-md" />
                <div className="flex-1 space-y-1.5">
                  <div className="bg-muted h-4 w-1/2 animate-pulse rounded-full" />
                  <div className="bg-muted h-3 w-1/3 animate-pulse rounded-full" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EmptyCalendar({ windowDays }: { windowDays: number }) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
        <CheckCircle2 className="size-8 text-emerald-500" aria-hidden="true" />
        <div>
          <p className="text-foreground text-sm font-semibold">All caught up!</p>
          <p className="text-muted-foreground mt-1 text-xs">
            No follow-ups scheduled in the next {windowDays} days. Open an enquiry to schedule the
            next one.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/admissions">Browse enquiries</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
